import {Component, HostListener, Optional} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {TRANSLATION_CRUD_SCOPE} from '../../../../../constants';
import {KEYS} from '../../../../../translation-keys';
import {ConditionGroup, ConditionRule, ConditionVariable, COMPARISONS, compileCondition, conditionIssue,
  emptyGroup, emptyRule, ruleIssue, valueType} from './workflow-condition-model';

import {VariableSelection} from '../../variables/workflow-variables';

export interface WorkflowConditionDialogComponent extends Translatable {}

/** Edits a detached step condition with searchable sources and explicitly grouped logic. */
@Component({selector: 'app-workflow-condition-dialog', templateUrl: './workflow-condition-dialog.component.html',
  styleUrls: ['./workflow-condition-dialog.component.css']})
@Translatable({scope: TRANSLATION_CRUD_SCOPE, keys: KEYS})
export class WorkflowConditionDialogComponent {
  public group: ConditionGroup = emptyGroup();
  public variables: ConditionVariable[] = [];
  public stageName = '';
  public originalSource = '';
  public existing = false;
  public choosing: ConditionRule = null;
  public createVariable: (pending: VariableSelection[]) => Promise<VariableSelection>;
  public pendingVariables: VariableSelection[] = [];
  public creating = false;
  public query = '';
  public category = '';
  public advanced = false;
  public operators = COMPARISONS;

  /** Injects the modal controller and administration translations. */
  public constructor(public activeModal: NgbActiveModal, @Optional() private translationService: TranslationService) {}

  /** Resolves a readable source while preserving missing references for repair. */
  public source(rule: ConditionRule): ConditionVariable { return this.variables.find(variable => variable.name === rule.variable); }

  /** Shows authored source labels with a technical fallback for missing variables. */
  public name(rule: ConditionRule): string { return this.source(rule)?.label || rule.variable || this._('Variable wählen'); }

  /** Opens a searchable source panel inside the same bounded dialog. */
  public choose(rule: ConditionRule): void { this.choosing = rule; this.query = ''; this.category = ''; }

  /** Filters friendly labels, workflow identifiers and source paths together. */
  public get matches(): ConditionVariable[] {
    const terms = this.query.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
    return this.variables.filter(variable => (!this.category || variable.group === this.category) &&
      terms.every(term => `${variable.label} ${variable.name} ${variable.detail}`.toLocaleLowerCase().includes(term)));
  }

  /** Adds a source to this draft and selects it without persisting anything before the outer Apply action. */
  public async addVariable(): Promise<void> {
    if (!this.createVariable || this.creating) { return; }
    this.creating = true;
    try {
      const selection = await this.createVariable(this.pendingVariables);
      if (!selection) { return; }
      this.pendingVariables.push(selection);
      this.variables = [...this.variables, selection.descriptor];
      this.select(selection.descriptor);
    } finally { this.creating = false; }
  }

  /** Initializes a typed comparison only after the user explicitly chooses a different source. */
  public select(variable: ConditionVariable): void {
    if (this.choosing.variable !== variable.name) {
      this.choosing.variable = variable.name;
      this.choosing.operator = '==';
      this.choosing.value = variable.valueType === 'boolean' ? true : variable.valueType === 'number' ? null : '';
    }
    this.choosing = null;
  }

  /** Appends a rule or a subgroup without mutating the workflow form. */
  public add(group: ConditionGroup, nested = false): void {
    group.children.push(nested ? {...emptyGroup(), children: [emptyRule()]} : emptyRule());
  }

  /** Removes a draft node and leaves empty subgroups visibly invalid until repaired. */
  public remove(group: ConditionGroup, index: number): void { group.children.splice(index, 1); }

  /** Deliberately clears the draft while leaving Apply and Cancel in control of persistence. */
  public clear(): void { this.group = emptyGroup(); this.originalSource = ''; }

  /** Preserves literal types of existing comparisons. */
  public type(rule: ConditionRule): string { return valueType(rule, this.variables); }

  /** Offers comparisons suited to the source and retains any existing advanced comparison. */
  public comparisons(rule: ConditionRule): string[] {
    const type = this.type(rule);
    const result = type === 'boolean' || type === 'string' ? ['==', '!='] : Object.keys(COMPARISONS);
    if (this.advanced) { return Object.keys(COMPARISONS); }
    if (!result.includes(rule.operator)) { result.push(rule.operator); }
    return result;
  }

  /** Maps configured calendar precision to a native date control. */
  public dateInput(rule: ConditionRule): string {
    const kind = this.source(rule)?.dateKind;
    return kind === 'fullDateTime' ? 'datetime-local' : kind === 'monthDate' ? 'month' : kind === 'yearDate' ? 'number' : 'date';
  }

  /** Displays wall-clock timestamp values without changing the stored timezone suffix on opening. */
  public dateValue(rule: ConditionRule): string {
    return String(rule.value || '').replace(/Z$|[+-]\d\d:\d\d$/, '');
  }

  /** Stores explicitly edited dates in the existing workflow string format. */
  public setDate(rule: ConditionRule, value: string): void { rule.value = String(value ?? ''); }

  /** Supplies a local, actionable error without removing the invalid rule. */
  public issue(rule: ConditionRule): string { return ruleIssue(rule, this.variables); }

  /** Disables applying incomplete groups or missing references. */
  public get validation(): string { return conditionIssue(this.group, this.variables); }

  /** Keeps unsupported original expressions visible without executing them. */
  public get expression(): string { return this.group ? this.validation ? '' : compileCondition(this.group) : this.originalSource; }

  /** Returns the complete draft in a single explicit transaction. */
  public save(): void { if (!this.validation && !this.choosing) { this.activeModal.close(this.group); } }

  /** Prevents global save shortcuts from persisting the workflow behind an open draft. */
  @HostListener('keydown', ['$event'])
  public keydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') { event.preventDefault(); event.stopPropagation(); }
  }
}
